import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Drawer, IconButton, Chip, CircularProgress, Alert,
  Divider, Tooltip, Stack, Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PsychologyIcon from '@mui/icons-material/Psychology';                  // 💭 deep thinking
import HubIcon from '@mui/icons-material/Hub';                                // 🧠 consultation
import DescriptionIcon from '@mui/icons-material/Description';                // 📝 formulation
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';          // ⏳ async pending
import ReplayIcon from '@mui/icons-material/Replay';                         // ↩ fallback
import LaunchIcon from '@mui/icons-material/Launch';

import { fetchReasoningTimeline } from '../../../services/gen2ReasoningApi';

// ── Glyph + label per event "family" ───────────────────────────────────────
// The timeline shows everything `notable`, but groups it visually so therapists
// can scan quickly. The same glyph is rendered next to the AI message bubble
// (see MessageArea) to bidirectionally cue events.
const EVENT_KIND = (e) => {
  if (e.fellBackToGen1 || e.fellBackToFastMode) return 'fallback';
  if (e.mode === 'consultation') return 'consultation';
  if (e.mode === 'deep_thinking') return 'deep_thinking';
  if (e.mode === 'fast_with_async') {
    return e.asyncStatus === 'pending' ? 'async_pending' : 'async_done';
  }
  if (e.formulationUpdated) return 'formulation_update';
  return 'fast';
};

const KIND_META = {
  fallback:           { Icon: ReplayIcon,         color: '#757575', label: 'Fallback' },
  consultation:       { Icon: HubIcon,            color: '#7b1fa2', label: 'Specialist consultation' },
  deep_thinking:      { Icon: PsychologyIcon,     color: '#1565c0', label: 'Deep thinking' },
  async_pending:      { Icon: HourglassEmptyIcon, color: '#ed6c02', label: 'Background work in progress' },
  async_done:         { Icon: HubIcon,            color: '#7b1fa2', label: 'Background work completed' },
  formulation_update: { Icon: DescriptionIcon,    color: '#2e7d32', label: 'Formulation updated' },
  fast:               { Icon: PsychologyIcon,     color: '#9e9e9e', label: 'Fast turn' }
};

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const FormulationDiffBlock = ({ diff }) => {
  if (!diff || !diff.hasChanges) {
    return <Typography variant="caption" color="text.secondary">No structural change recorded.</Typography>;
  }
  return (
    <Box sx={{ mt: 0.5 }}>
      {Object.entries(diff.sections).map(([key, section]) => (
        <Box key={key} sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={600}>{section.label}</Typography>
          {section.changeType === 'added' && (
            <Typography variant="body2" sx={{ fontSize: 12, color: 'success.dark' }}>
              + {section.after}
            </Typography>
          )}
          {section.changeType === 'removed' && (
            <Typography variant="body2" sx={{ fontSize: 12, color: 'error.dark', textDecoration: 'line-through' }}>
              − {section.before}
            </Typography>
          )}
          {section.changeType === 'changed' && (
            <>
              <Typography variant="body2" sx={{ fontSize: 12, color: 'error.dark', textDecoration: 'line-through' }}>
                − {section.before}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 12, color: 'success.dark' }}>
                + {section.after}
              </Typography>
            </>
          )}
          {section.added?.length > 0 && section.added.map((item, i) => (
            <Typography key={`a-${i}`} variant="body2" sx={{ fontSize: 12, color: 'success.dark' }}>
              + {item.description || item.process || JSON.stringify(item)}
              {item.domain && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>({item.domain})</Typography>}
            </Typography>
          ))}
          {section.removed?.length > 0 && section.removed.map((item, i) => (
            <Typography key={`r-${i}`} variant="body2" sx={{ fontSize: 12, color: 'error.dark', textDecoration: 'line-through' }}>
              − {item.description || item.process || JSON.stringify(item)}
            </Typography>
          ))}
          {section.changed?.length > 0 && section.changed.map((item, i) => (
            <Typography key={`c-${i}`} variant="body2" sx={{ fontSize: 12 }}>
              ~ {item.description || item.process || JSON.stringify(item)}
            </Typography>
          ))}
        </Box>
      ))}
    </Box>
  );
};

const TimelineEvent = ({ event, expanded, onToggle, onJumpToChat }) => {
  const kind = EVENT_KIND(event);
  const { Icon, color, label } = KIND_META[kind];
  const time = fmtTime(event.createdAt);
  const isAsyncPending = kind === 'async_pending';

  return (
    <Box sx={{
      position: 'relative',
      pl: 4,
      pb: 2.5,
      borderLeft: '2px solid',
      borderColor: 'divider',
      ml: 1.5
    }}>
      {/* glyph */}
      <Box sx={{
        position: 'absolute', left: -13, top: 0,
        width: 26, height: 26, borderRadius: '50%',
        backgroundColor: color, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: 1
      }}>
        <Icon sx={{ fontSize: 16 }} />
      </Box>

      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{time}</Typography>
        <Chip label={label} size="small" sx={{ height: 18, fontSize: 10, backgroundColor: color, color: 'white' }} />
        {event.formulationVersion && (
          <Chip
            label={event.formulationUpdated
              ? `Formulation v${event.formulationVersion} (updated)`
              : `Formulation v${event.formulationVersion}`}
            size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }}
          />
        )}
        {isAsyncPending && (
          <CircularProgress size={12} thickness={5} />
        )}
      </Box>

      {/* narrative */}
      {event.narrative && (
        <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.4, mb: 0.5 }}>
          {event.narrative}
        </Typography>
      )}

      {/* client excerpt */}
      {event.clientMessageExcerpt && (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mb: 1 }}>
          “{event.clientMessageExcerpt.slice(0, 100)}{event.clientMessageExcerpt.length > 100 ? '…' : ''}”
        </Typography>
      )}

      {/* actions */}
      <Stack direction="row" spacing={1}>
        <Button size="small" onClick={onToggle} sx={{ textTransform: 'none', fontSize: 12 }}>
          {expanded ? 'Hide details' : 'Expand'}
        </Button>
        {onJumpToChat && (
          <Button
            size="small"
            startIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
            onClick={onJumpToChat}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            Jump to chat
          </Button>
        )}
      </Stack>

      {/* expanded body */}
      {expanded && (
        <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
          {event.triggerReason && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              <strong>Why this fired:</strong> {event.triggerReason}
            </Typography>
          )}

          {event.consultationSummary?.moduleKey && (
            <>
              <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 1 }}>
                Specialist module
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 12 }}>
                {event.consultationSummary.moduleKey}
              </Typography>
              {event.consultationSummary.suggestedApproach && (
                <>
                  <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 1 }}>
                    Recommendation
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {event.consultationSummary.suggestedApproach}
                  </Typography>
                </>
              )}
              {event.consultationSummary.recommendations?.length > 0 && (
                <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0, fontSize: 12 }}>
                  {event.consultationSummary.recommendations.map((r, i) => (
                    <li key={i}>{typeof r === 'string' ? r : (r.text || JSON.stringify(r))}</li>
                  ))}
                </Box>
              )}
            </>
          )}

          {event.reasoningOutput?.clinicalAnalysis && (
            <>
              <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 1 }}>
                Clinical analysis
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {event.reasoningOutput.clinicalAnalysis}
              </Typography>
            </>
          )}
          {event.reasoningOutput?.responsePlan && (
            <>
              <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 1 }}>
                Response plan
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {event.reasoningOutput.responsePlan}
              </Typography>
            </>
          )}

          {event.formulationDiff?.hasChanges && (
            <>
              <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 1 }}>
                Formulation changes (v{event.formulationDiff.prevVersion ?? '—'} → v{event.formulationDiff.currVersion})
              </Typography>
              <FormulationDiffBlock diff={event.formulationDiff} />
            </>
          )}

          {(event.fellBackToFastMode || event.fellBackToGen1) && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: 12, py: 0.5 }}>
              {event.fellBackToGen1
                ? 'Gen 2 reasoning failed; fell back to Gen 1 pipeline.'
                : 'Deep reasoning timed out; fell back to fast mode.'}
            </Alert>
          )}

          {event.unavailableModules?.length > 0 && (
            <Tooltip title="The semantic analysis suggested these modules, but they aren't enabled for this persona.">
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                Suggested but unavailable: {event.unavailableModules.join(', ')}
              </Typography>
            </Tooltip>
          )}

          {event.latencyMs != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Processed in {(event.latencyMs / 1000).toFixed(1)}s
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Right-side slide-out timeline. Lists only notable events. Auto-refreshes
 * on the `server:gen2:trace_updated` socket event so fast_with_async
 * completions appear without manual reload.
 */
const ReasoningTimeline = ({ open, onClose, conversationId, socket, token, onJumpToEventTime, gen2ApiBase = 'therapist' }) => {
  const [events, setEvents] = useState([]);
  const [gen2Enabled, setGen2Enabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!conversationId) return;
    try {
      setLoading(true);
      const data = await fetchReasoningTimeline(conversationId, token, gen2ApiBase);
      setEvents(data.events || []);
      setGen2Enabled(data.gen2Enabled !== false);
      setError('');
    } catch (err) {
      console.error('[ReasoningTimeline] load error:', err);
      setError(err?.response?.data?.msg || 'Could not load reasoning timeline.');
    } finally {
      setLoading(false);
    }
  }, [conversationId, token, gen2ApiBase]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Live refresh when async work completes.
  useEffect(() => {
    if (!socket || !conversationId) return;
    const handler = (payload) => {
      if (payload?.conversationId === String(conversationId)) {
        load();
      }
    };
    socket.on('server:gen2:trace_updated', handler);
    return () => socket.off('server:gen2:trace_updated', handler);
  }, [socket, conversationId, load]);

  const emptyState = useMemo(() => {
    if (loading) return null;
    if (!gen2Enabled) return 'This conversation is not Gen 2 enabled — no reasoning trace.';
    if (events.length === 0) return 'No notable reasoning events yet. Routine fast-mode turns are hidden here; expand any AI message in chat to inspect raw mode data.';
    return null;
  }, [loading, gen2Enabled, events.length]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 0, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Sticky header so the close button stays reachable regardless of scroll */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 1,
        backgroundColor: 'background.paper',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        p: 2, borderBottom: '1px solid', borderColor: 'divider'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Reasoning Timeline</Typography>
        <Tooltip title="Close (Esc)">
          <IconButton onClick={onClose} aria-label="Close timeline" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ p: 2, overflow: 'auto', flexGrow: 1 }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {emptyState && (
          <Typography variant="body2" color="text.secondary">{emptyState}</Typography>
        )}
        {!loading && events.length > 0 && (
          <Box sx={{ pt: 1 }}>
            {events.map((e) => (
              <TimelineEvent
                key={e._id}
                event={e}
                expanded={expandedId === e._id}
                onToggle={() => setExpandedId(expandedId === e._id ? null : e._id)}
                onJumpToChat={onJumpToEventTime ? () => onJumpToEventTime(e.createdAt) : null}
              />
            ))}
            <Divider sx={{ mt: 2 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Showing {events.length} notable event{events.length === 1 ? '' : 's'}. Routine fast-mode turns are filtered out.
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ReasoningTimeline;
